import {
  CanvasDocumentData,
  ExtrasDocumentData,
  FormatsDocumentData,
} from "prismicio-types";

type WithoutNull<T> = {
  [P in keyof T]: Exclude<T[P], null>;
};

type CanvasFallbackContent = WithoutNull<CanvasDocumentData>;
type FormatsFallbackContent = WithoutNull<FormatsDocumentData>;
type ExtrasFallbackContent = WithoutNull<ExtrasDocumentData>;

export const CANVAS_FALLBACK_CONTENT: CanvasFallbackContent = {
  subtotal_text: [
    {
      type: "paragraph",
      text: "Subtotal (Excluding delivery)",
      spans: [
        {
          start: 0,
          end: 8,
          type: "strong",
        },
      ],
    },
  ],
  one_page_button_label: "One page",
  two_page_button_label: "Two page",
  form_unknown_error_message:
    "Sorry, something went wrong. We've logged the problem but you might want to try again.",
  form_validation_error_message:
    "There's something missing! Check what's wrong and try again",
  form_submit_button_label: "Continue",
  form_cancel_button_label: "Cancel",
  form_loading_label: "Loading...",
  default_edit_page_label: "Edit page",
  start_again_button_label: "Start again",
  form_field_required_error_message: "Oops! Don't forget to fill this in",
  form_field_min_length_error_message: "This is a little short. Add some more!",
  form_field_max_length_error_message: "Whoops! This is too long for the page.",
  form_field_pattern_error_message:
    "Whoops! This contains unsupported characters. Try again.",
  form_field_unknown_error_message:
    "Sorry, we aren't sure why this has failed. Please try again later.",
  form_field_disabled_message:
    "Sorry, you can't edit this field for a book you've already purchased.",
  form_upload_photo_empty_title: "Upload a photo",
  form_upload_photo_empty_subtitle:
    "(Choose the highest quality photos you have)",
  form_upload_photo_invalid:
    "Your photo may look blurry when printed. You can still continue, or you may want to try a higher quality photo.",
  form_upload_photo_valid: "Fabulous! This photo will look splendid",
  form_upload_photo_crop_label: "Crop",
  form_upload_photo_delete_label: "Delete",
  form_upload_photo_error_message: "Sorry, we couldn't upload your photo",
  form_upload_photo_retry_label: "Retry",
  form_upload_photo_rejected_message:
    "Sorry, your photo was flagged by our content moderation. Please try a different photo.",
  form_upload_photo_try_another_label: "Try another",
  add_another_button_label: "+ Add another",
  characters_left_label: "{{number}} characters left",
  back_previous_step_link: "Back to the previous step",
  back_product_page_link: "Back to the product page",
  creation_flow_step_count_label: "Step {{currentStep}} of {{numberOfSteps}}",
  form_field_selection_error_message: "Oops! Don't forget to select an option",
  form_field_too_few_error_message:
    "Please select at least {{minOptions}} options",
  form_field_too_many_error_message:
    "Please select no more than {{maxOptions}} options",
  creation_flow_submit_button_label: "Continue",
  edit_progress_label: "Edit {{currentEdit}} of {{numberOfEdits}}",
  next_edit_button_label: "Next edit",
  previous_edit_button_label: "Previous edit",
  free_upsell_label: "FREE",
  back_to_cart_button_label: "Back to cart",
  cart_error_subtitle:
    "You might need to log in or this product may have already been ordered",
  cart_error_title: "Sorry, this product can't be edited",
  cart_item_not_found_error_subtitle:
    "Please go back to your cart and try again",
  cart_item_not_found_error_title: "Sorry, we couldn't find that item",
  back_to_orders_button_label: "Back to orders",
  order_error_subtitle: "It might have passed the edit window or be paused",
  order_error_title: "Sorry, this order can't be edited",
  order_item_not_found_error_subtitle:
    "Please go back to your orders and try again.",
  order_item_not_found_error_title: "Sorry, we couldn't find that item",
  month_names: [
    {
      january: "January",
      february: "February",
      march: "March",
      april: "April",
      may: "May",
      june: "June",
      july: "July",
      august: "August",
      september: "September",
      october: "October",
      november: "November",
      december: "December",
    },
  ],
};

export const FORMATS_FALLBACK_CONTENT: FormatsFallbackContent = {
  formats_submit_button_label: "Add to cart",
  formats_update_button_label: "Update item",
  formats_title: "Choose a format for your book",
  formats_subtitle:
    "Make your gift even more glorious with our selection of cover options",
  hardback_badge: "Most popular",
  hardback_button_label: "Select Hardcover",
  hardback_description:
    "A premium hardcover with a quality matte finish. Made to be read again and again.",
  hardback_selected_button_label: "Hardcover selected",
  hardback_title: "Hardcover",
  hardback_sticker: "",
  layflat_badge: "Lifetime keepsake",
  layflat_button_label: "Select Premium Hardcover",
  layflat_description:
    "Luxurious pages that lay perfectly flat when reading - all wrapped up in a sturdy cover for an unforgettable gift.",
  layflat_selected_button_label: "Premium Hardcover selected",
  layflat_title: "Premium Hardcover Layflat",
  layflat_sticker: "",
  softback_badge: "",
  softback_button_label: "Select Softcover",
  softback_description:
    "A lovely, lightweight softcover printed on thick, durable paper.",
  softback_selected_button_label: "Softcover selected",
  softback_title: "Softcover",
  softback_sticker: "",
  overrides: [],
};

export const EXTRAS_FALLBACK_CONTENT: ExtrasFallbackContent = {
  giftwrap_title: "Wrap it up in magic",
  giftwrap_subtitle:
    "Make your gift even more glorious with a gorgeously designed gift wrap",
  giftwrap_submit_button_label: "Continue",
  giftwrapSlices: [],
  addons_title: "Make your gift even more fun-nomenal",
  addons_subtitle: "Add one or more of these delightful extras",
  addons_submit_button_label: "Continue",
  addOnSlices: [],
  addons_quick_look_button_label: "Look inside",
  quick_look_add_button_label: "Add to order",
  quick_look_cancel_button_label: "No thanks",
};
